import type { Paginated } from './api';
import type { CreateAdminCustomerRequest } from './customer';
import type { DeskPackageConfiguration } from './rate-card';
import type { ResourceKind } from './resource';
import type { GuestBookingRequestDetails, GuestRequestedPaymentMethod } from './guest-booking';

export const BOOKING_PAYMENT_METHODS = ['cash', 'whish', 'omt', 'other'] as const;
export type BookingPaymentMethod = (typeof BOOKING_PAYMENT_METHODS)[number];

export type BookingAllocationType = 'desk' | 'room';
export type BookingLifecycleStatus = 'payment_pending' | 'confirmed' | 'cancelled';
export type BookingBalanceFilter = 'outstanding' | 'settled';
export type BookingListStatus = 'unpaid' | 'confirmed' | 'completed';
export type BookingDisplayStatus =
  'payment_pending' | 'confirmed' | 'deposit_due' | 'completed' | 'cancelled';
export type BookingPaymentStatus = 'unpaid' | 'deposit' | 'paid';
export type PackageEligibilityStatus = 'not_required' | 'pending' | 'verified';

export interface BookingResourceSummary {
  id: string;
  slug: string;
  name: string;
  kind: ResourceKind;
}

export interface BookingDeskSummary {
  id: string;
  deskId: string;
}

export interface BookingCustomerSummary {
  id: string;
  reference: string;
  name: string;
  email: string;
  phone: string;
}

export interface BookingPriceSnapshot {
  rateCardVersionId: string;
  itemId: string;
  itemName: string;
  unit: 'hour' | 'day' | 'week' | 'month' | 'block';
  unitAmountMinor: number;
  quantity: number;
  totalAmountMinor: number;
  currency: 'USD';
  /** Present only for desk-package bookings and frozen at booking creation. */
  package: DeskPackageConfiguration | null;
}

export interface BookingPackageEligibility {
  status: PackageEligibilityStatus;
  evidenceNote: string;
  verifiedAt: string | null;
  verifiedByUserId: string | null;
}

export interface BookingPaymentRecord {
  id: string;
  reference: string;
  amountMinor: number;
  currency: 'USD';
  method: BookingPaymentMethod;
  methodDetail: string;
  methodLabel: string;
  note: string;
  recordedByUserId: string | null;
  recordedAt: string;
}

export interface BookingPaymentSummary {
  status: BookingPaymentStatus;
  totalAmountMinor: number;
  paidAmountMinor: number;
  balanceAmountMinor: number;
  requiredDepositAmountMinor: number;
  depositPercentage: 50;
  latestMethod: BookingPaymentMethod | null;
  latestMethodLabel: string | null;
}

export interface AdminBooking {
  id: string;
  reference: string;
  allocationType: BookingAllocationType;
  lifecycleStatus: BookingLifecycleStatus;
  status: BookingDisplayStatus;
  /** Inclusive start date of the reservation. */
  bookingDate: string;
  /** Inclusive end date; equal to `bookingDate` for rooms and daily desks. */
  bookingEndDate: string;
  startMinute: number | null;
  endMinute: number | null;
  resource: BookingResourceSummary;
  desk: BookingDeskSummary | null;
  customer: BookingCustomerSummary;
  price: BookingPriceSnapshot;
  packageEligibility: BookingPackageEligibility;
  payment: BookingPaymentSummary;
  payments: BookingPaymentRecord[];
  internalNote: string;
  cancelledAt: string | null;
  cancelledByUserId: string | null;
  cancellationReason: string;
  bookedAt: string;
  createdByUserId: string | null;
  updatedAt: string;
  guestRequest: {
    requestId: string;
    requestedPaymentMethod: GuestRequestedPaymentMethod | null;
    details: GuestBookingRequestDetails | null;
  } | null;
}

export interface ConfirmAdminBookingRequest {
  settlement?: 'deposit' | 'full';
  method?: BookingPaymentMethod;
  methodDetail?: string;
  note?: string;
  packageEligibilityVerification?: {
    evidenceNote: string;
  };
}

export type UpdateAdminBookingRequest =
  | {
      bookingDate: string;
      deskId: string;
      deskPackageItemId: string;
      requestedPaymentMethod: 'cash' | 'whish';
    }
  | {
      bookingDate: string;
      deskId: string;
      deskPackageItemId: 'free-trial';
      startMinute: number;
      endMinute: number;
      requestedPaymentMethod: 'none';
    }
  | {
      bookingDate: string;
      startMinute: number;
      endMinute: number;
      requestedPaymentMethod: 'cash' | 'whish';
    };

/**
 * The same body as `POST /admin/customers`, created inside the booking so a
 * failed booking leaves no customer behind. Omitted channels start without
 * marketing consent.
 */
export type NewBookingCustomerRequest = CreateAdminCustomerRequest;

export type CreateAdminBookingRequest = {
  resourceId: string;
  bookingDate: string;
  deskId?: string;
  deskPackageItemId?: string;
  startMinute?: number;
  endMinute?: number;
  internalNote?: string;
  /** May be recorded before payment; required before paying for a restricted package. */
  packageEligibilityVerification?: {
    evidenceNote: string;
  };
  initialPayment?: {
    amountMinor: number;
    method: BookingPaymentMethod;
    methodDetail?: string;
  };
} & (
  | { customerId: string; newCustomer?: never }
  | { customerId?: never; newCustomer: NewBookingCustomerRequest }
);

export interface CancelAdminBookingRequest {
  reason?: string;
  /** Defaults off for public requests and on for regular bookings. */
  sendCancellationEmail?: boolean;
}

export interface RecordAdminBookingPaymentRequest {
  amountMinor: number;
  expectedPaidAmountMinor: number;
  method: BookingPaymentMethod;
  methodDetail?: string;
  note?: string;
  /** Required on the first payment while package eligibility is pending. */
  packageEligibilityVerification?: {
    evidenceNote: string;
  };
}

export interface BookingAvailabilityOccupant {
  id: string;
  reference: string;
  customerName: string;
}

export interface DeskAvailability {
  id: string;
  deskId: string;
  isActive: boolean;
  available: boolean;
  booking: BookingAvailabilityOccupant | null;
}

export interface TimeSlotAvailability {
  startMinute: number;
  endMinute: number;
  available: boolean;
  booking: BookingAvailabilityOccupant | null;
}

export interface AdminBookingAvailability {
  date: string;
  resource: BookingResourceSummary;
  slotMinutes: number | null;
  openFromMinute: number | null;
  openToMinute: number | null;
  desks: DeskAvailability[];
  slots: TimeSlotAvailability[];
}

export interface AdminBookingListSummary {
  bookingCount: number;
  totalAmountMinor: number;
  paidAmountMinor: number;
  outstandingAmountMinor: number;
  outstandingBookingCount: number;
  unpaidBookingCount: number;
  depositDueBookingCount: number;
  currency: 'USD';
}

export interface AdminBookingListResponse extends Paginated<AdminBooking> {
  summary?: AdminBookingListSummary;
}
export interface AdminBookingResponse {
  data: AdminBooking;
}
export interface AdminBookingAvailabilityResponse {
  data: AdminBookingAvailability;
}
